// The per-ITEM tracking sheet — carrier, tracking number and ETA for one line
// of an order. Extracted from PurchasingQueuePage.js at Session 79.14c.
//
// PER ITEM, not per order, and that is load-bearing rather than incidental: it
// writes material_request_items. #410's reasoning for keeping the VENDOR'S
// ORDER NUMBER out of this sheet turns on exactly that — an order-level fact on
// a per-line sheet is wrong the first time a request has two lines.
//
// Fully controlled: every value and every setter arrives as a prop and nothing
// here touches the database. That is what makes it testable in isolation.

import React from 'react';
import Overlay from '../Overlay';
import { useTier, usesPhoneLayout } from '../../lib/tiers';
import { itemLabel } from '../../lib/requestItems';

export const CARRIERS = ['UPS', 'FedEx', 'USPS', 'Freight', 'Other'];
export default function TrackingSheet({ visible, req, item, isMobile, trackCarrier, setTrackCarrier, trackNumber, setTrackNumber, trackEta, setTrackEta, trackSaving, trackError, trackDone, trackCleared, onSubmit, onClear, onClose }) {
  // BEFORE the early return, and that is not style. A hook after a conditional
  // `return null` is called on some renders and not others, which breaks the
  // order React relies on -- rules-of-hooks, and it fails the BUILD rather than
  // a test, because no test imports this component.
  const { tier } = useTier();

  if (!visible || !req || !item) return null;
  const itemName = itemLabel(item);

  // #250 / #148 Split C, Session 79.14. Converted to Overlay.js - locked rule 3.
  //
  // #505 FIXED HERE AT SESSION 79.15b, and the deferral above was right to wait.
  // This was ALWAYS a bottom sheet -- its container had no width branch at all --
  // so it rose from the bottom of a 1440px monitor. 79.14c deliberately left it
  // alone rather than settle a design question inside a plumbing refactor, and
  // #417's D24 has now settled it.
  //
  // THE BOUNDARY IS 1024, NOT THE `isMobile` 768 THIS COMPONENT IS HANDED.
  // D24 as CORRECTED at approval: sheet on Phone AND TABLET, modal on Desk and
  // Wide. Once Colby ruled that a tablet is drawn for the CARRIED case, handing
  // it a centred modal contradicts the tier it lives in -- and a bottom sheet is
  // actively better on a carried device, because it rises into thumb reach and
  // needs no precise aim. The `isMobile` prop stays: it still drives this
  // sheet's internal layout, which is a different question from which side it
  // comes from.
  return (
    <Overlay
      variant={usesPhoneLayout(tier) ? 'sheet' : 'modal'}
      zIndex={4000}
      onClose={onClose}
      panelStyle={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflowY: 'hidden', overflowX: 'hidden' }}
    >
      <>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 16px', flexShrink: 0 }} />
        {trackDone ? (
          <div style={{ padding: '0 20px 40px' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>{trackCleared ? 'Tracking cleared' : 'Tracking saved'}</div>
            </div>
            <button onClick={onClose} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <div style={{ padding: '0 20px 40px', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>{item.tracking_number ? 'Edit tracking' : 'Add tracking'}</div>
            <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>{req.request_number} · {itemName}</div>
            {trackError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{trackError}</div>}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Carrier</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {CARRIERS.map(c => (
                <button key={c} onClick={() => setTrackCarrier(c)}
                  style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${trackCarrier === c ? '#1D9E75' : '#E0E0E0'}`, background: trackCarrier === c ? '#E1F5EE' : 'white', color: trackCarrier === c ? '#085041' : '#5C5C5C', fontSize: 12, fontWeight: trackCarrier === c ? 700 : 400, cursor: 'pointer' }}>
                  {c}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Tracking number</div>
            <input type="text" value={trackNumber} onChange={e => setTrackNumber(e.target.value)} placeholder="Enter tracking number"
              style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', marginBottom: 16, boxSizing: 'border-box' }} />

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>ETA <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
            <input type="date" value={trackEta} onChange={e => setTrackEta(e.target.value)}
              style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }} />

            <button onClick={onSubmit} disabled={trackSaving}
              style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: trackSaving ? '#E0E0E0' : '#0F0F0F', color: trackSaving ? '#9A9A9A' : 'white', cursor: trackSaving ? 'not-allowed' : 'pointer', marginBottom: 8 }}>
              {trackSaving ? 'Saving...' : 'Save tracking'}
            </button>
            {item.tracking_number && (
              <button onClick={onClear} disabled={trackSaving} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#A32D2D', cursor: trackSaving ? 'not-allowed' : 'pointer', padding: 4, marginBottom: 4 }}>Clear tracking</button>
            )}
            <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
          </div>
        )}
      </>
    </Overlay>
  );
}
